/* Pure UI helpers for My Surveys (Figma CrewKnit spec).
   Display math only — no API calls, no hooks, no JSX. */

import type { MySurveyStatus } from "../components/survey/types";

/** Progress % from answered/total (Figma card bars). */
export function surveyProgressPct(answered?: number, total?: number): number {
  if (!answered || !total || total <= 0) return 0;
  return Math.round((answered / total) * 100);
}

/** Figma note line under the title ("Not started · 12 questions", "5 of 8 answered"). */
export function surveyNoteLine(status: MySurveyStatus, answered?: number, total?: number): string {
  const n = total ?? 0;
  const a = answered ?? 0;
  if (status === "completed") return `Submitted · ${a} of ${n} answered`;
  if (a > 0) return `${a} of ${n} answered`;
  return `Not started · ${n} ${n === 1 ? "question" : "questions"} to answer`;
}

/** Status pill tokens (Figma dc.html:7233 mapping + overdue kept red). */
export function surveyStatusMeta(status: MySurveyStatus): { label: string; pillBg: string; pillColor: string } {
  switch (status) {
    case "pending":
      return { label: "Pending", pillBg: "bg-[var(--color-warning-light)]", pillColor: "text-[var(--color-warning-dark)]" };
    case "inProgress":
      return { label: "In Progress", pillBg: "bg-[var(--color-primary-100)]", pillColor: "text-[var(--color-primary-700)]" };
    case "completed":
      return { label: "Completed", pillBg: "bg-[var(--color-success-light)]", pillColor: "text-[var(--color-success-dark)]" };
    case "overdue":
    default:
      return { label: "Overdue", pillBg: "bg-[var(--color-error-light)]", pillColor: "text-[var(--color-error-dark)]" };
  }
}

/** Figma CTA link per status (labels match current frontend behavior). */
export function surveyCta(status: MySurveyStatus): { label: string; icon: "arrow" | "file" } {
  switch (status) {
    case "inProgress":
      return { label: "Continue survey", icon: "arrow" };
    case "completed":
      return { label: "View my answers", icon: "file" };
    case "overdue":
      return { label: "Start Now", icon: "arrow" };
    case "pending":
    default:
      return { label: "Start survey", icon: "arrow" };
  }
}
